import { Mail, Phone, Trash2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../ui/alert-dialog";

const STATUSES = ["new", "read", "replied"];
const BADGE = {
  new: "bg-[var(--ec-lake)] text-white",
  read: "bg-[var(--ec-foam)] text-[var(--ec-navy)]",
  replied: "bg-[var(--ec-green-deep)] text-white",
};

export default function MessageCard({ message: m, onStatus, onDelete }) {
  const when = new Date(m.created_at).toLocaleString();
  return (
    <li className="bg-white rounded-2xl p-6 border border-[var(--ec-sand)]" data-testid={`admin-message-${m.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-xl text-[var(--ec-navy)]">{m.name}</h2>
          <p className="text-xs text-[var(--ec-charcoal)]/75 mt-1">
            {m.interest} · {when}
            {m.email_status && (
              <span data-testid={`admin-message-email-status-${m.id}`}> · {{ failed: "Email not delivered", sent_test: "Test email sent" }[m.email_status] || "Emailed to you"}</span>
            )}
          </p>
        </div>
        <span className={`text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full ${BADGE[m.status] || BADGE.read}`} data-testid={`admin-message-status-${m.id}`}>
          {m.status}
        </span>
      </div>
      <p className="mt-4 text-sm text-[var(--ec-charcoal)] whitespace-pre-wrap break-words">{m.message}</p>
      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm">
        <a href={`mailto:${m.email}`} className="inline-flex items-center gap-1.5 text-[var(--ec-lake)] hover:underline break-all" data-testid={`admin-message-email-${m.id}`}>
          <Mail className="w-4 h-4 shrink-0" /> {m.email}
        </a>
        {m.phone && (
          <a href={`tel:${m.phone}`} className="inline-flex items-center gap-1.5 text-[var(--ec-lake)] hover:underline">
            <Phone className="w-4 h-4" /> {m.phone}
          </a>
        )}
      </div>
      <div className="mt-5 pt-4 border-t border-[var(--ec-sand)] flex flex-wrap items-center justify-between gap-3">
        <label className="text-sm text-[var(--ec-navy)] flex items-center gap-2">
          Status
          <select
            value={m.status}
            onChange={(e) => onStatus(m.id, e.target.value)}
            className="bg-[var(--ec-cream)] border border-[var(--ec-field-border)] rounded-lg px-3 py-1.5"
            data-testid={`admin-message-status-select-${m.id}`}
          >
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button type="button" className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-700 hover:underline" data-testid={`admin-message-delete-${m.id}`}>
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this message?</AlertDialogTitle>
              <AlertDialogDescription>This permanently removes the message from {m.name}. This can't be undone.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="admin-delete-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => onDelete(m.id)} className="bg-red-700 hover:bg-red-800" data-testid="admin-delete-confirm">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </li>
  );
}
